import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { useCallback, useContext, useMemo, useRef } from 'react'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import TourV2SearchGroupItems from 'components/Search/TourV2/TourV2SearchGroupItems'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import LineUsersMultiIcon from 'LuxKit/Icons/line/LineUsersMultiIcon'
import { TOURS_GROUP_TYPE } from 'constants/tours'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

export function getTourGroupTypesList(intl: IntlShape): Array<App.Tours.TourItemGroupType> {
  return [
    {
      id: TOURS_GROUP_TYPE.isSmallGroup,
      name: intl.formatMessage({
        id: 'RTa5_pR0yMhX_EBQ6KqN',
        defaultMessage: 'Small Group Tours',
        description: 'Label for small group tours filter option on the tour page',
      }),
      subtitle: intl.formatMessage({
        id: '5HCk_t2A9GHDiJeRV2Zr',
        defaultMessage: 'Join a group of up to 24 travellers',
        description: 'Description for small group tours filter option on the form',
      }),
      icon: <LineUsersAltIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isLargeGroup,
      name: intl.formatMessage({
        id: 'VZs1bwM1r8Bc5zJtmSfk',
        defaultMessage: 'Large Group Tours',
        description: 'Label for large group tours filter option on the tour page',
      }),
      subtitle: intl.formatMessage({
        id: 'uc7Rl7Kk49sByBGLKho3',
        defaultMessage: 'Join a group of 24+ travellers',
        description: 'Description for large group tours filter option on the form',
      }),
      icon: <LineUsersMultiIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isPrivateRequest,
      name: intl.formatMessage({
        id: 'B938QyaFM6banZutZrJQ',
        defaultMessage: 'Private Requests Available',
        description: 'Label for private requests tour filter option on the form',
      }),
      subtitle: intl.formatMessage({
        id: 'J5O2oO77YK-wgeaRHQMV',
        defaultMessage: 'Travel exclusively with your family and friends',
        description: 'Description for private requests tour filter option',
      }),
      icon: <LineGuidedtoursIcon />,
    },
    {
      id: TOURS_GROUP_TYPE.isFamilyFriendly,
      name: intl.formatMessage({
        id: 'kkQ4Kch7N_rknEL2wS0z',
        defaultMessage: 'Family Friendly',
        description: 'Label for family friendly tour filter option on the tour page',
      }),
      subtitle: intl.formatMessage({
        id: '-fXTPQ-PX-K3OunoaY4E',
        defaultMessage: 'Great for both adults and children',
        description: 'Description for family friendly tour filter option',
      }),
      icon: <LineKidIcon />,
    },
  ]
}

export const tourGroupTypeIds = [
  TOURS_GROUP_TYPE.isSmallGroup,
  TOURS_GROUP_TYPE.isLargeGroup,
  TOURS_GROUP_TYPE.isPrivateRequest,
  TOURS_GROUP_TYPE.isFamilyFriendly,
]

export const getTourGroupSelectedText = (tourGroupTypes: Array<App.Tours.TourGroupType>, intl: IntlShape) => {
  const selectedNames = getTourGroupTypesList(intl).reduce<Array<string>>((acc, tourGroupType) => {
    if (tourGroupTypes.includes(tourGroupType.id)) acc.push(tourGroupType.name)
    return acc
  }, [])
  return intl.formatList(selectedNames, { type: 'disjunction' })
}

interface Props {
  isToggled: boolean
  onGroupToggle: (...args: any) => void
  onApply: (...args: any) => void
}

function TourV2SearchGroupSelect(props: Props) {
  const { isToggled, onGroupToggle, onApply } = props
  const intl = useIntl()

  const { tourGroupTypes = [] } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const tourGroupTypesList = useMemo(() => getTourGroupTypesList(intl), [intl])

  const resetGroupType = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SELECT_ALL_TOUR_GROUP_TYPE })
  }, [globalSearchDispatch])

  const tourGroupTypeSelectedText = useMemo(() => getTourGroupSelectedText(tourGroupTypes, intl), [tourGroupTypes, intl])

  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const groupLabel = intl.formatMessage({
    id: '-op5cwQyutON11K660F4',
    defaultMessage: 'Type of group',
    description: 'Label for tour group type search filter field on the form',
  })
  const groupPlaceholder = intl.formatMessage({
    id: 'JWuXtmWukD-SXoTYE8GC',
    defaultMessage: 'e.g Small Group Tours, Private Requests Available...',
    description: 'Placeholder text for tour group type search filter field',
  })
  const resetLabel = intl.formatMessage({
    id: 'vqRAEnfVcdyJog2bjcrq',
    defaultMessage: 'Reset',
    description: 'Reset button text for tour group type filter on the tour page',
  })
  const applyLabel = intl.formatMessage({
    id: '78kbfKudeZyanF3LTpfQ',
    defaultMessage: 'Apply',
    description: 'Apply button text for tour group type filter on the tour page',
  })

  return (<>
    <SearchFormField
      ref={dropdownTriggerRef}
      onClick={onGroupToggle}
      label={groupLabel}
      placeholder={groupPlaceholder}
      value={tourGroupTypeSelectedText}
    />
    <DropdownList
      size="fill-anchor"
      anchorRef={dropdownTriggerRef}
      triggerRef={dropdownTriggerRef}
      open={isToggled}
      onClose={onGroupToggle}
      secondaryActionProps={{
        kind: 'tertiary',
        children: resetLabel,
        onClick: resetGroupType,
        'data-testid': 'search-tour-grouptype-reset-button',
      }}
      primaryActionProps={{
        children: applyLabel,
        onClick: onApply,
        'data-testid': 'search-tour-grouptype-apply-button',
      }}
    >
      {isToggled && <TourV2SearchGroupItems groupTypes={tourGroupTypesList} />}
    </DropdownList>
  </>

  )
}

export default TourV2SearchGroupSelect
